// RFID Service for ESP32/Arduino Communication
// 
// For USB Serial (Testing): Use the bridge server (arduino-bridge/server.js)
//   - Bridge server runs on http://localhost:3001
//   - Set RFID_READER_IP_ADDRESS to 'localhost' or '127.0.0.1'
//   - Set RFID_READER_PORT to 3001
//
// For Network (Ethernet/WiFi): Direct connection
//   - Update RFID_READER_IP_ADDRESS with your ESP32/Arduino's IP address
//   - Set RFID_READER_PORT to 80

// For USB Serial via Bridge Server (Testing):
const RFID_READER_IP_ADDRESS = 'localhost'; // or '127.0.0.1'
const RFID_READER_PORT = 3001;

// For Direct Network Connection (Production):
// const RFID_READER_IP_ADDRESS = '192.168.1.100'; // Your ESP32/Arduino IP
// const RFID_READER_PORT = 80;

const RFID_READER_BASE_URL = `http://${RFID_READER_IP_ADDRESS}:${RFID_READER_PORT}`;

export interface RFIDStatus {
    status: 'idle' | 'waiting' | 'card_read' | 'error';
    cardNumber?: string;
    message?: string;
}

/**
 * Start card registration mode on ESP32
 * Returns a promise that resolves when a card is read
 */
export const startCardRegistration = async (): Promise<string> => {
    try {
        // Send command to RFID reader to start listening for card
        const response = await fetch(`${RFID_READER_BASE_URL}/api/register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action: 'start' }),
        });

        if (!response.ok) {
            throw new Error('Failed to start card registration');
        }

        // Poll for card read (RFID reader will return card number when read)
        return await pollForCard();
    } catch (error) {
        console.error('Error starting card registration:', error);
        throw new Error('Failed to connect to RFID reader. Please check if device is connected.');
    }
};

/**
 * Poll RFID reader for card read status
 */
const pollForCard = async (): Promise<string> => {
    const maxAttempts = 300; // 5 minutes (300 * 1 second)
    let attempts = 0;

    console.log('🔄 Starting to poll for card...');

    while (attempts < maxAttempts) {
        try {
            const response = await fetch(`${RFID_READER_BASE_URL}/api/status`, {
                method: 'GET',
            });

            if (!response.ok) {
                console.warn('⚠️ Status check failed, retrying...');
                await new Promise(resolve => setTimeout(resolve, 1000));
                attempts++;
                continue;
            }

            const data: RFIDStatus = await response.json();
            console.log('📊 Status:', data.status, data.cardNumber || '');

            if (data.status === 'card_read' && data.cardNumber) {
                console.log('✅ Card detected:', data.cardNumber);
                // Reset RFID reader status
                await fetch(`${RFID_READER_BASE_URL}/api/reset`, { method: 'POST' });
                return data.cardNumber;
            }

            if (data.status === 'error') {
                throw new Error(data.message || 'Error reading card');
            }

            // Wait 1 second before next poll
            await new Promise(resolve => setTimeout(resolve, 1000));
            attempts++;
        } catch (error) {
            console.error('Error polling for card:', error);
            // Continue polling even on error
            await new Promise(resolve => setTimeout(resolve, 1000));
            attempts++;
        }
    }

    throw new Error('Timeout: No card detected within 5 minutes');
};

/**
 * Cancel card registration
 */
export const cancelCardRegistration = async (): Promise<void> => {
    try {
        await fetch(`${RFID_READER_BASE_URL}/api/cancel`, {
            method: 'POST',
        });
    } catch (error) {
        console.error('Error canceling card registration:', error);
    }
};

/**
 * Get last swiped card for attendance marking
 */
export const getLastSwipedCard = async (): Promise<string | null> => {
    try {
        const response = await fetch(`${RFID_READER_BASE_URL}/api/attendance`, {
            method: 'GET',
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        if (data.status === 'card_swiped' && data.cardNumber) {
            return data.cardNumber;
        }

        return null;
    } catch (error) {
        console.error('Error getting last swiped card:', error);
        return null;
    }
};

/**
 * Check if a card number is already registered
 */
export const checkCardExists = async (cardNumber: string, collection: 'staff' | 'teachers'): Promise<boolean> => {
    try {
        // This will be checked in the component using Firebase query
        // For now, return false (actual check happens in component)
        return false;
    } catch (error) {
        console.error('Error checking card:', error);
        return false;
    }
};

